/**
 * Story SF-CHAT-6.2 — o modo vigente, dito de forma inequívoca.
 *
 * A story existe para que nunca se acredite estar consumindo a API oficial
 * estando em stub, **nem o contrário**. Os dois enganos custam caro e são
 * simétricos, então o vocabulário aqui tem quatro estados, não dois: fixture,
 * API oficial, live recusado e desconhecido.
 *
 * `live` só é verdadeiro quando o runtime **montou** o transporte. Anunciar
 * "consumindo API real" porque alguém escreveu `MCP_BACKEND_MODE=live` num
 * ambiente que recusou seria a mentira simétrica da que a story impede.
 */

export interface RuntimeHealth {
  backend_mode?: unknown;
  payables_transport?: unknown;
}

export interface RuntimeMode {
  live: boolean;
  unavailable: boolean;
  unknown: boolean;
  label: string;
}

export function describeRuntimeMode(runtime: RuntimeHealth | null | undefined): RuntimeMode {
  const modo = typeof runtime?.backend_mode === "string" ? runtime.backend_mode : "";
  const transporte =
    typeof runtime?.payables_transport === "string" ? runtime.payables_transport : "";

  if (!modo || !transporte) {
    // Sem saúde lida, não se presume nada. Presumir fixture é o engano barato.
    return { live: false, unavailable: false, unknown: true, label: "Modo desconhecido" };
  }
  if (transporte === "live") {
    return { live: true, unavailable: false, unknown: false, label: "API oficial" };
  }
  if (modo === "live") {
    return {
      live: false,
      unavailable: true,
      unknown: false,
      label: "Modo live indisponível",
    };
  }
  return { live: false, unavailable: false, unknown: false, label: "Fixtures locais" };
}

export interface EnvironmentBanner {
  live: boolean;
  unavailable: boolean;
  unknown: boolean;
  title: string;
  body: string;
}

export function describeEnvironmentBanner(
  runtime: RuntimeHealth | null | undefined,
): EnvironmentBanner {
  const modo = describeRuntimeMode(runtime);

  if (modo.live) {
    return {
      ...modo,
      title: "Consumo da API oficial ativo.",
      body:
        "As consultas desta conversa vão para a API do aplicativo principal e devolvem dados reais " +
        "da empresa configurada. Ações de aprovação de contas só acontecem depois de uma prévia e do botão Confirmar.",
    };
  }
  if (modo.unavailable) {
    return {
      ...modo,
      title: "Modo live pedido, mas indisponível.",
      body:
        "A configuração pede a API oficial e o runtime recusou por falta de credencial. " +
        "As rotas de sessão respondem 503 — o chat não caiu em dados simulados.",
    };
  }
  if (modo.unknown) {
    return {
      ...modo,
      title: "Modo do ambiente não confirmado.",
      body:
        "Não foi possível ler a saúde do runtime, então o modo vigente é desconhecido. " +
        "Não envie dados pessoais, documentos ou informações financeiras reais.",
    };
  }
  return {
    ...modo,
    title: "Ambiente interno de teste.",
    body:
      "Dados simulados, modo stub e sem ações financeiras. " +
      "Não envie dados pessoais, documentos ou informações financeiras reais.",
  };
}

/**
 * Story SF-CHAT-6.5 — qual tela de chat montar.
 *
 * `live` só quando o backend declara modo live **e** não está em fixture. Com
 * saúde ilegível, fica a tela de fixture, que falha com mensagem própria e não
 * pede senha a ninguém.
 */
export function chooseChatSurface(
  runtime: (RuntimeHealth & { fixture_mode?: unknown }) | null | undefined,
): "live" | "fixture" {
  return runtime?.backend_mode === "live" && runtime?.fixture_mode !== true ? "live" : "fixture";
}
